import type { MaybeElementRef, UseStorageOptions } from "@vueuse/core";
import type { RefOrGetter } from "ls/common/types";
import type { OrganizationJs } from "@/generated/models";
import { createInjectionState, useLocalStorage, useToggle } from "@vueuse/core";
import { useCloudBreakpoints } from "ls/composables/useCloudBreakpoints";
import { useContextOrThrow } from "ls/composables/useContextOrThrow";
import { defineStore } from "pinia";
import { computed, ref, toValue } from "vue";
import { clamp, round } from "@/common/lib/number";

const [useProvideNavigationContext, useNavigationContextFn] = createInjectionState(() => {
  const browseWorkspacesOrganization = ref<OrganizationJs>();
  return {
    browseWorkspacesOrganization,
  };
});

export { useProvideNavigationContext };
export const useNavigationContext = useContextOrThrow(useNavigationContextFn);

type SidebarMode = "fixed" | "floating" | "drawer";
const defaultSidebarWidth = 320;
const minSidebarWidth = 256;
const maxSidebarWidth = 512;

function useOptionalLocalStorage(key: string, defaultValue: boolean, { persistWhen, storageOptions }: { persistWhen: RefOrGetter<boolean>; storageOptions?: UseStorageOptions<boolean> } = { persistWhen: ref(true) }) {
  const local = ref(false);
  const persistent = useLocalStorage(key, defaultValue, storageOptions);

  const value = computed({
    get: () => {
      if (toValue(persistWhen)) {
        return persistent.value;
      }

      return local.value;
    },
    set: (value: boolean) => {
      if (toValue(persistWhen)) {
        persistent.value = value;
      } else {
        local.value = value;
      }
    },
  });

  const toggle = useToggle(value);

  return {
    value,
    toggle,
  };
}

export const useSidebarStore = defineStore("sidebar", () => {
  const mode = useLocalStorage<SidebarMode>("sidebar", "fixed", { listenToStorageChanges: false });

  function hide() {
    mode.value = "floating";
  }

  function toggle() {
    mode.value = mode.value === "fixed" ? "floating" : "fixed";
  }

  // only persist pane open state on atLeastSm breakpoint
  // so that smaller screens (mobile) always start with panes closed and it resets on next visit
  const { atLeastSm } = useCloudBreakpoints();
  const { value: detailsPaneOpen, toggle: detailsPaneToggle } = useOptionalLocalStorage("details-pane", true, {
    persistWhen: atLeastSm,
    storageOptions: { listenToStorageChanges: false },
  });

  const { value: activityPaneOpen, toggle: activityPaneToggle } = useOptionalLocalStorage("activity-pane", true, {
    persistWhen: atLeastSm,
    storageOptions: { listenToStorageChanges: false },
  });

  const width = useLocalStorage<number>("sidebar-width", defaultSidebarWidth, { listenToStorageChanges: false });

  function setSidebarWidth(value: number) {
    width.value = round(clamp(value, minSidebarWidth, maxSidebarWidth));
  }

  function resetSidebarWidth() {
    width.value = defaultSidebarWidth;
  }

  return {
    mode,
    hide,
    toggle,
    detailsPaneOpen,
    detailsPaneToggle,
    activityPaneOpen,
    activityPaneToggle,
    width: computed(() => mode.value === "fixed" ? width.value : defaultSidebarWidth),
    setSidebarWidth,
    resetSidebarWidth,
  };
});

export interface EditableNameRef {
  start: () => void;
  linkElement: MaybeElementRef;
}

const [useProvideSidebarContext, useSidebarContextFn] = createInjectionState(() => {
  const editableNameRefs = new Map<string, EditableNameRef>();

  function registerEditableNameRef(id: string, ref: EditableNameRef) {
    if (ref == null) {
      editableNameRefs.delete(id);
    } else {
      editableNameRefs.set(id, ref);
    }
  }

  const menuObservers = ref(new Set<RefOrGetter<boolean | undefined>>());
  function observeMenu(isOpen: RefOrGetter<boolean | undefined>) {
    menuObservers.value.add(isOpen);

    return () => {
      menuObservers.value.delete(isOpen);
    };
  };

  const anyMenuOpen = computed(() => {
    return [...menuObservers.value].some(menuOpen => toValue(menuOpen));
  });

  return {
    observeMenu,
    anyMenuOpen,
    registerEditableNameRef,
    editableNameRefs,
  };
});

export { useProvideSidebarContext };
export const useSidebarContext = useContextOrThrow(useSidebarContextFn);
export const useSidebarContextUnsafe = useSidebarContextFn;
